/**
 * Core math for the Rocket League physics reimplementation.
 *
 * COORDINATE SYSTEM (matches RLBot / RLUtilities / RocketSim, NOT Three.js):
 *   +X  -> toward the orange-side right wall  (side walls at x = +/-4096)
 *   +Y  -> toward the orange goal             (back walls at y = +/-5120)
 *   +Z  -> up                                 (floor z = 0, ceiling z = 2044)
 *
 * Orientation is stored as a 3x3 rotation matrix whose COLUMNS are the car's
 * local axes in world space:
 *   col0 = forward, col1 = left, col2 = up
 * This matches RLUtilities' `mat3` convention and is required by the aerial
 * control model, which is written as tau = Theta * (T*u + D * Theta^T * omega).
 *
 * Three.js uses Y-up and a different handedness; conversion happens exactly
 * once, at the render boundary (see src/render/convert.ts). Physics code must
 * never import Three.js.
 *
 * All values are in Rocket League "unreal units" (uu). 1 uu ~ 1 cm.
 * Angles are radians, time is seconds.
 */

export type Vec3 = { x: number; y: number; z: number };

// ---------------------------------------------------------------------------
// Vec3 — allocating helpers (readable) plus in-place variants (hot paths)
// ---------------------------------------------------------------------------

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const clone = (a: Vec3): Vec3 => ({ x: a.x, y: a.y, z: a.z });

export const copy = (out: Vec3, a: Vec3): Vec3 => {
  out.x = a.x;
  out.y = a.y;
  out.z = a.z;
  return out;
};

export const set = (out: Vec3, x: number, y: number, z: number): Vec3 => {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
};

export const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });

export const addTo = (out: Vec3, b: Vec3): Vec3 => {
  out.x += b.x;
  out.y += b.y;
  out.z += b.z;
  return out;
};

/** out += b * s */
export const addScaledTo = (out: Vec3, b: Vec3, s: number): Vec3 => {
  out.x += b.x * s;
  out.y += b.y * s;
  out.z += b.z * s;
  return out;
};

export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });

export const subFrom = (out: Vec3, b: Vec3): Vec3 => {
  out.x -= b.x;
  out.y -= b.y;
  out.z -= b.z;
  return out;
};

export const scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });

export const scaleBy = (out: Vec3, s: number): Vec3 => {
  out.x *= s;
  out.y *= s;
  out.z *= s;
  return out;
};

export const neg = (a: Vec3): Vec3 => ({ x: -a.x, y: -a.y, z: -a.z });

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

export const lengthSq = (a: Vec3): number => a.x * a.x + a.y * a.y + a.z * a.z;

export const length = (a: Vec3): number => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);

export const dist = (a: Vec3, b: Vec3): number => length(sub(a, b));

export const distSq = (a: Vec3, b: Vec3): number => lengthSq(sub(a, b));

/** Normalize; returns the zero vector when the input length is below `eps`. */
export const normalize = (a: Vec3, eps = 1e-9): Vec3 => {
  const l = length(a);
  return l < eps ? vec3(0, 0, 0) : { x: a.x / l, y: a.y / l, z: a.z / l };
};

export const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
});

/** Clamp a vector's magnitude to `max`, preserving direction. */
export const clampLength = (a: Vec3, max: number): Vec3 => {
  const l = length(a);
  return l > max && l > 0 ? scale(a, max / l) : clone(a);
};

/** In-place magnitude clamp; returns true when the clamp actually applied. */
export const clampLengthBy = (out: Vec3, max: number): boolean => {
  const l = length(out);
  if (l > max && l > 0) {
    scaleBy(out, max / l);
    return true;
  }
  return false;
};

/** Component of `a` along the unit vector `n`. */
export const projectOnto = (a: Vec3, n: Vec3): Vec3 => scale(n, dot(a, n));

/** Component of `a` perpendicular to the unit vector `n`. */
export const rejectFrom = (a: Vec3, n: Vec3): Vec3 => sub(a, scale(n, dot(a, n)));

export const ZERO: Readonly<Vec3> = Object.freeze(vec3(0, 0, 0));
export const UP: Readonly<Vec3> = Object.freeze(vec3(0, 0, 1));

// ---------------------------------------------------------------------------
// Mat3 — row-major 3x3, indexed m[row * 3 + col]
// ---------------------------------------------------------------------------

/** Row-major 3x3 matrix: m[row * 3 + col]. */
export type Mat3 = Float64Array;

export const mat3 = (): Mat3 => new Float64Array(9);

export const identity3 = (): Mat3 => {
  const m = new Float64Array(9);
  m[0] = 1;
  m[4] = 1;
  m[8] = 1;
  return m;
};

export const cloneMat3 = (m: Mat3): Mat3 => new Float64Array(m) as Mat3;

/** Column `c` of `m` — for an orientation matrix: 0 = forward, 1 = left, 2 = up. */
export const col = (m: Mat3, c: 0 | 1 | 2): Vec3 => ({ x: m[c], y: m[3 + c], z: m[6 + c] });

export const setCol = (m: Mat3, c: 0 | 1 | 2, v: Vec3): Mat3 => {
  m[c] = v.x;
  m[3 + c] = v.y;
  m[6 + c] = v.z;
  return m;
};

export const forward = (m: Mat3): Vec3 => col(m, 0);
export const left = (m: Mat3): Vec3 => col(m, 1);
export const up = (m: Mat3): Vec3 => col(m, 2);

/** Build an orientation matrix from its local axes (columns). */
export const fromAxes = (fwd: Vec3, lft: Vec3, u: Vec3): Mat3 => {
  const m = new Float64Array(9) as Mat3;
  setCol(m, 0, fwd);
  setCol(m, 1, lft);
  setCol(m, 2, u);
  return m;
};

/** m * v */
export const mulVec = (m: Mat3, v: Vec3): Vec3 => ({
  x: m[0] * v.x + m[1] * v.y + m[2] * v.z,
  y: m[3] * v.x + m[4] * v.y + m[5] * v.z,
  z: m[6] * v.x + m[7] * v.y + m[8] * v.z,
});

/** m^T * v — world-space vector into the matrix's local frame. */
export const mulVecT = (m: Mat3, v: Vec3): Vec3 => ({
  x: m[0] * v.x + m[3] * v.y + m[6] * v.z,
  y: m[1] * v.x + m[4] * v.y + m[7] * v.z,
  z: m[2] * v.x + m[5] * v.y + m[8] * v.z,
});

/** a * b */
export const mulMat = (a: Mat3, b: Mat3): Mat3 => {
  const m = new Float64Array(9) as Mat3;
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      m[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
    }
  }
  return m;
};

export const transpose = (a: Mat3): Mat3 => {
  const m = new Float64Array(9) as Mat3;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) m[r * 3 + c] = a[c * 3 + r];
  return m;
};

/**
 * Rotation matrix from an axis-angle vector (magnitude = angle in radians),
 * via Rodrigues' formula. This is the matrix exponential of the skew-symmetric
 * matrix of `w`, and is how RLUtilities integrates orientation:
 *   Theta(t + dt) = axisToRotation(omega * dt) * Theta(t)
 */
export const axisToRotation = (w: Vec3): Mat3 => {
  const theta = length(w);
  if (theta < 1e-12) return identity3();

  const { x, y, z } = scale(w, 1 / theta);
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const t = 1 - c;

  const m = new Float64Array(9) as Mat3;
  m[0] = t * x * x + c;
  m[1] = t * x * y - s * z;
  m[2] = t * x * z + s * y;
  m[3] = t * x * y + s * z;
  m[4] = t * y * y + c;
  m[5] = t * y * z - s * x;
  m[6] = t * x * z - s * y;
  m[7] = t * y * z + s * x;
  m[8] = t * z * z + c;
  return m;
};

/** Inverse of `axisToRotation`: recover the axis-angle vector from a rotation. */
export const rotationToAxis = (m: Mat3): Vec3 => {
  const trace = m[0] + m[4] + m[8];
  const cosTheta = Math.max(-1, Math.min(1, (trace - 1) * 0.5));
  const theta = Math.acos(cosTheta);
  if (theta < 1e-9) return vec3(0, 0, 0);
  const scl = theta / (2 * Math.sin(theta));
  return vec3((m[7] - m[5]) * scl, (m[2] - m[6]) * scl, (m[3] - m[1]) * scl);
};

/**
 * `axisToRotation(scale(w, dt))`, written into `out`.
 *
 * Bit-for-bit the same as the allocating pair — same operation order, same
 * intermediate rounding — because every numeric test in the project is pinned
 * to the old one. It exists purely to stop the per-tick orientation update from
 * allocating: `integrateRotation` below used to produce three Float64Array(9)
 * and six Vec3 per car per tick.
 */
export const axisToRotationScaledInto = (out: Mat3, w: Vec3, dt: number): Mat3 => {
  const wx = w.x * dt;
  const wy = w.y * dt;
  const wz = w.z * dt;
  const theta = Math.sqrt(wx * wx + wy * wy + wz * wz);
  if (theta < 1e-12) {
    out.fill(0);
    out[0] = 1;
    out[4] = 1;
    out[8] = 1;
    return out;
  }

  const inv = 1 / theta;
  const x = wx * inv;
  const y = wy * inv;
  const z = wz * inv;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const t = 1 - c;

  out[0] = t * x * x + c;
  out[1] = t * x * y - s * z;
  out[2] = t * x * z + s * y;
  out[3] = t * x * y + s * z;
  out[4] = t * y * y + c;
  out[5] = t * y * z - s * x;
  out[6] = t * x * z - s * y;
  out[7] = t * y * z + s * x;
  out[8] = t * z * z + c;
  return out;
};

/** `a * b` into `out`. `out` must not alias `a` or `b`. */
export const mulMatInto = (out: Mat3, a: Mat3, b: Mat3): Mat3 => {
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
    }
  }
  return out;
};

/** `orthonormalize(m)` into `out`. `out` may alias `m`. */
export const orthonormalizeInto = (out: Mat3, m: Mat3): Mat3 => {
  // forward = normalize(col 0)
  let fx = m[0];
  let fy = m[3];
  let fz = m[6];
  let len = Math.sqrt(fx * fx + fy * fy + fz * fz);
  if (len < 1e-9) {
    fx = 0;
    fy = 0;
    fz = 0;
  } else {
    fx /= len;
    fy /= len;
    fz /= len;
  }

  // up = normalize(col 2 with the forward component removed)
  const ux0 = m[2];
  const uy0 = m[5];
  const uz0 = m[8];
  const d = ux0 * fx + uy0 * fy + uz0 * fz;
  let ux = ux0 - fx * d;
  let uy = uy0 - fy * d;
  let uz = uz0 - fz * d;
  len = Math.sqrt(ux * ux + uy * uy + uz * uz);
  if (len < 1e-9) {
    ux = 0;
    uy = 0;
    uz = 0;
  } else {
    ux /= len;
    uy /= len;
    uz /= len;
  }

  // left = cross(up, forward)
  const lx = uy * fz - uz * fy;
  const ly = uz * fx - ux * fz;
  const lz = ux * fy - uy * fx;

  out[0] = fx;
  out[3] = fy;
  out[6] = fz;
  out[1] = lx;
  out[4] = ly;
  out[7] = lz;
  out[2] = ux;
  out[5] = uy;
  out[8] = uz;
  return out;
};

/** Scratch for `integrateRotation`. The module is single-threaded. */
const _rotDelta = new Float64Array(9) as Mat3;
const _rotNext = new Float64Array(9) as Mat3;

/**
 * `Theta <- orthonormalize(axisToRotation(omega * dt) * Theta)`, in place.
 *
 * The single hottest allocation site in the simulation before it existed: every
 * live car ran it once per tick, and once more inside ./ground when that module
 * owns the integration.
 */
export const integrateRotation = (orientation: Mat3, omega: Vec3, dt: number): void => {
  axisToRotationScaledInto(_rotDelta, omega, dt);
  mulMatInto(_rotNext, _rotDelta, orientation);
  orthonormalizeInto(orientation, _rotNext);
};

/**
 * Re-orthonormalize a rotation matrix (Gram-Schmidt on the columns).
 * Accumulated floating-point error in the per-tick orientation integration
 * makes the matrix drift out of SO(3); left unchecked this shears the car.
 */
export const orthonormalize = (m: Mat3): Mat3 => {
  let f = normalize(col(m, 0));
  let u = normalize(rejectFrom(col(m, 2), f));
  const l = cross(u, f);
  return fromAxes(f, l, u);
};

/**
 * Orientation from Unreal-style Euler angles, matching RLBot's
 * (pitch, yaw, roll) convention.
 */
export const eulerToRotation = (pitch: number, yaw: number, roll: number): Mat3 => {
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cr = Math.cos(roll);
  const sr = Math.sin(roll);

  return fromAxes(
    // forward
    vec3(cp * cy, cp * sy, sp),
    // left
    vec3(cy * sp * sr - cr * sy, sy * sp * sr + cr * cy, -cp * sr),
    // up
    vec3(-cr * cy * sp - sr * sy, -cr * sy * sp + sr * cy, cp * cr)
  );
};

export const rotationToEuler = (m: Mat3): { pitch: number; yaw: number; roll: number } => ({
  pitch: Math.atan2(m[6], Math.hypot(m[0], m[3])),
  yaw: Math.atan2(m[3], m[0]),
  roll: Math.atan2(-m[7], m[8]),
});

// ---------------------------------------------------------------------------
// Scalar helpers
// ---------------------------------------------------------------------------

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const sign = (v: number): number => (v > 0 ? 1 : v < 0 ? -1 : 0);

/**
 * Piecewise-linear lookup over a table of [input, output] pairs sorted by
 * input. Clamps at both ends. Rocket League's throttle-acceleration and
 * steering-curvature curves are both defined this way, so getting the
 * clamping and interpolation right here is load-bearing for car feel.
 */
export const piecewiseLinear = (table: readonly (readonly [number, number])[], x: number): number => {
  const n = table.length;
  if (n === 0) return 0;
  if (x <= table[0][0]) return table[0][1];
  if (x >= table[n - 1][0]) return table[n - 1][1];

  // Tables here are <= 8 rows; a linear scan beats binary search.
  for (let i = 1; i < n; i++) {
    const [x1, y1] = table[i];
    if (x <= x1) {
      const [x0, y0] = table[i - 1];
      const span = x1 - x0;
      return span === 0 ? y1 : y0 + ((y1 - y0) * (x - x0)) / span;
    }
  }
  return table[n - 1][1];
};
